/**
 * Web-side ad model. Mirrors the `SerializedAd` shape returned by the
 * `getAds` callable (see `@highnote/server/src/functions/handlers/ads/getAds.ts`).
 *
 * Keep this shape in lock-step with the server contract — the backend is
 * the authoritative source of truth. All optional string fields are
 * explicitly nullable to match the serializer.
 */
export type AdType = "sponsored" | "brandPartner" | "spacePinned";

export type Ad = {
  id: string;
  imageUrl: string;
  text: string;
  linkUrl: string;
  advertiserName: string;
  advertiserAvatarUrl: string | null;
  isActive: boolean;
  priority: number;
  adType: AdType;
  campaignId: string | null;
  title: string | null;
  body: string | null;
  /**
   * Optional call-to-action label. When present, the CTA button triggers
   * the link open; otherwise, the entire card is the click target.
   */
  ctaText: string | null;
  /**
   * Plumbed through for the forthcoming Kevel integration. The web client
   * does NOT fire these URLs yet — tracking is a follow-up task.
   */
  impressionTrackingUrl: string | null;
  clickTrackingUrl: string | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const asStringOrNull = (value: any): string | null =>
  typeof value === "string" ? value : null;

/**
 * Normalises an arbitrary stringy `adType` into the narrow [AdType]
 * union. We've seen docs in the wild with casing variants (e.g.
 * `"brandpartner"`, `"brand_partner"`, `"brand-partner"`,
 * `"space_pinned"`), and silently falling back to `"sponsored"`
 * caused the brand-partner layout to render as a sponsored-comment
 * card. Strip separators + lowercase before comparing so all common
 * variants resolve to the canonical camelCase value.
 */
const asAdType = (value: unknown): AdType => {
  if (typeof value !== "string") return "sponsored";
  const normalized = value.toLowerCase().replace(/[_\-\s]/g, "");
  if (normalized === "brandpartner") return "brandPartner";
  if (normalized === "spacepinned") return "spacePinned";
  if (normalized === "sponsored") return "sponsored";
  return "sponsored";
};

/**
 * Safely normalizes an unknown payload from the `getAds` callable into an
 * [Ad]. The raw value is assumed to already be a `SerializedAd` from the
 * server; this normalization only guards against `null`/`undefined` slipping
 * through and keeps the narrow `Ad` type enforceable on the web client.
 */
export const adFromMap = (raw: Record<string, unknown>): Ad => ({
  id: String(raw.id ?? ""),
  imageUrl: String(raw.imageUrl ?? ""),
  text: String(raw.text ?? ""),
  linkUrl: String(raw.linkUrl ?? ""),
  advertiserName: String(raw.advertiserName ?? ""),
  advertiserAvatarUrl: asStringOrNull(raw.advertiserAvatarUrl),
  isActive: raw.isActive !== false,
  priority: typeof raw.priority === "number" ? raw.priority : 0,
  adType: asAdType(raw.adType),
  campaignId: asStringOrNull(raw.campaignId),
  title: asStringOrNull(raw.title),
  body: asStringOrNull(raw.body),
  ctaText: asStringOrNull(raw.ctaText),
  impressionTrackingUrl: asStringOrNull(raw.impressionTrackingUrl),
  clickTrackingUrl: asStringOrNull(raw.clickTrackingUrl),
});
